import { extractGifFrameCanvases } from "./gifFrameExtractor";
import { dimensionCache } from "./caching/DimensionCache";

export const getImageSourceWithFallback = ({
  optimalSource,
  fallbackSource,
  callback
}) => {
  // try to get the optimal image
  cacheImage(optimalSource).then(
    () => {
      // if the optimal image can be cached then use it
      return callback(optimalSource);
    },
    () => {
      // if the optimal image can not be cached then use the original source as a fallback
      return callback(fallbackSource);
    }
  );
};

export const getGifUploadData = async file => {
  const imageUrl = window.URL.createObjectURL(file);
  const frameData = await extractGifFrameCanvases(imageUrl);
  let duration = 0;
  let frames = 0;
  frameData.forEach(frame => {
    // GIF frame delay is represented in 1/100 of a second. Multiplying this by 10 converts it to milliseconds
    duration += frame.frameInfo.delay * 10;
    frames++;
  });

  // When an uploaded gif has 0 frame delay it will cause the duration to return 0
  // Browsers play GIFs back at a default frame delay of 100ms if no frame delay is provided.
  if (duration === 0 && frames > 1) {
    duration = frames * 100;
  }

  return {
    duration
  };
};

const loadImageSize = src => {
  return new Promise((resolve, reject) => {
    let img = new Image();
    img.onload = () => {
      // when an image has no intrinsic dimensions it is treated differently in each browser in terms of
      // base dimensions, and firefox for example will set width and height to 0 by default
      // we will set default for these to 150 to avoid having issues inserting these elements
      resolve({ height: img.height || 150, width: img.width || 150 });
    };
    img.onerror = reject;
    img.src = src;
  });
};

/**
 * Get image size from source URL, throwing on failure.
 * Use this when you need to handle errors and try fallback URLs.
 */
export const getImageSizeFromSrcOrThrow = async (src, timeout = 5000) => {
  const timeoutPromise = new Promise((_, reject) => {
    setTimeout(() => {
      reject(new Error(`Image dimension loading timed out after ${timeout}ms`));
    }, timeout);
  });

  return await Promise.race([loadImageSize(src), timeoutPromise]);
};

export const getImageSizeFromSrc = async (src, timeout = 5000) => {
  try {
    return await getImageSizeFromSrcOrThrow(src, timeout);
  } catch (error) {
    console.warn(`Failed to load image dimensions from ${src}:`, error.message);
    // Return default dimensions on timeout or error
    return { height: 600, width: 800 };
  }
};

/**
 * Get image size from source with caching support
 * Checks cache first, then falls back to loading if not cached
 */
export const getImageSizeFromSrcWithCache = async (src, timeout = 5000) => {
  if (!src) {
    return { height: 150, width: 150 };
  }

  // Check cache first
  const cached = dimensionCache.getDimensions(src);
  if (cached) {
    return cached;
  }

  // Load dimensions - only cache successful loads to avoid poisoning the cache
  // with default dimensions that would cause wrong aspect ratios on subsequent lookups
  try {
    const dimensions = await getImageSizeFromSrcOrThrow(src, timeout);
    dimensionCache.setDimensions(src, dimensions);
    return dimensions;
  } catch {
    return { height: 600, width: 800 };
  }
};

/**
 *
 * @param {Object} imageElement an object representing the image element to get dimensions for
 * @returns {Object} imageDimensions - contains width and height dimensions for an image
 */
export const getImageDimensionsFromElement = async imageElement => {
  // check for dimensions in the media object first
  if (
    imageElement.media &&
    imageElement.media.width &&
    imageElement.media.height
  ) {
    const imageDimensions = {
      width: imageElement.media.width,
      height: imageElement.media.height
    };
    // Cache media dimensions for future use
    const src =
      imageElement.url ||
      imageElement.src ||
      imageElement.originalUrl ||
      imageElement.originalSrc ||
      imageElement.previewUrl ||
      imageElement.previewSrc;
    if (src) {
      dimensionCache.setDimensions(src, imageDimensions);
    }
    return imageDimensions;
  }

  // Try URLs in order of preference, falling back on failure
  const urlsToTry = [
    imageElement.url || imageElement.src,
    imageElement.originalUrl || imageElement.originalSrc,
    imageElement.previewUrl || imageElement.previewSrc
  ].filter(Boolean);

  // Deduplicate URLs to avoid redundant load attempts
  const uniqueUrls = [...new Set(urlsToTry)];

  for (const url of uniqueUrls) {
    // Check cache first
    const cached = dimensionCache.getDimensions(url);
    if (cached) {
      return cached;
    }

    try {
      const imageDimensions = await getImageSizeFromSrcOrThrow(url);
      dimensionCache.setDimensions(url, imageDimensions);
      return imageDimensions;
    } catch {
      // Continue to next URL
    }
  }

  // All URLs failed - use defaults as last resort
  return { height: 600, width: 800 };
};

export const cacheImage = src => {
  return new Promise((resolve, reject) => {
    const image = new Image();

    image.src = src;
    image.onload = () => {
      resolve(true);
    };

    image.onerror = () => {
      reject(false);
    };
  });
};

export const isImageCached = src => {
  const image = new Image();
  image.src = src;

  return image.complete;
};
